import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSessionStatus, useToggleSession } from '../../hooks/useSession';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/currency';
import CloseSession from './CloseSession';

export default function OpenSession() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';

  const { data: session, isLoading } = useSessionStatus();
  const toggleSession = useToggleSession();

  const [openingCash, setOpeningCash] = useState('');
  const [showClose, setShowClose] = useState(false);
  const [error, setError] = useState('');

  async function handleOpen(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setError('');

    try {
      await toggleSession.mutateAsync({
        shopid: shop.id,
        action: 'open',
        opening_cash: parseFloat(openingCash) || 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open session');
    }
  }

  if (isLoading) {
    return <div className="page"><p>Loading session status...</p></div>;
  }

  if (session?.status === 'open') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">{t('session', 'Session')}</h1>
        </div>

        <div className="session-open-card">
          <div className="session-status session-status--open">
            <span className="session-status__dot" />
            Session Open
          </div>
          <div className="session-info">
            <div className="session-info__row">
              <span>Session ID</span>
              <span>{session.id}</span>
            </div>
            <div className="session-info__row">
              <span>Opened at</span>
              <span>{session.opening_datetime ? new Date(session.opening_datetime).toLocaleString() : '—'}</span>
            </div>
            <div className="session-info__row">
              <span>{t('opening_cash', 'Opening Cash')}</span>
              <span>{formatCurrency(session.opening_cash ?? 0, currSym)}</span>
            </div>
          </div>

          <div className="session-actions">
            <Link to="/session/cashflow" className="btn btn--outline">
              {t('cashflow', 'Cash Flow')}
            </Link>
            <button
              className="btn btn--danger"
              onClick={() => setShowClose(true)}
            >
              Close Session
            </button>
          </div>
        </div>

        {showClose && (
          <CloseSession
            sessionId={session.id}
            onClosed={() => setShowClose(false)}
          />
        )}
      </div>
    );
  }

  // No active session — show open form
  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('session', 'Session')}</h1>
      </div>

      <div className="session-closed-card">
        <div className="session-status session-status--closed">
          <span className="session-status__dot" />
          No Active Session
        </div>
        <p className="session-closed-card__info">
          Open a session to start recording sales.
        </p>

        <form onSubmit={handleOpen} className="session-open-form">
          <div className="form-group">
            <label className="form-label">{t('opening_cash', 'Opening Cash')}</label>
            <input
              type="number"
              className="form-input form-input--lg"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min={0}
              autoFocus
            />
          </div>

          {error && <div className="alert alert--error">{error}</div>}

          <button
            type="submit"
            className="btn btn--primary btn--full"
            disabled={toggleSession.isPending}
          >
            {toggleSession.isPending ? 'Opening...' : 'Open Session'}
          </button>
        </form>
      </div>
    </div>
  );
}
